import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { BasePage } from "components/BasePage/BasePage";
import { ExternalLinkIcon } from "@heroicons/react/outline";
import { useUpdateProjectPayload } from "hooks/useProjects";
import { useProjectDetails } from "pages/Details/hooks/useProjectDetails";
import MultiSelectInput from "components/inputs/MultiSelectInput";
import { ConformityModule } from "./types";

interface NotifiedBodiesRouteParams {
  projectId: string;
}

export const NotifiedBodies = () => {
  const { t } = useTranslation();
  const { projectId } = useParams<NotifiedBodiesRouteParams>();
  const { project, fetchInProgress } = useProjectDetails(projectId);
  const [notifiedBodyName, setNotifiedBodyName] = useState("");
  const [notifiedBodyNumber, setNotifiedBodyNumber] = useState("");
  const [notifiedBodyAddress, setNotifiedBodyAddress] = useState("");
  const [notifiedBodyEmail, setNotifiedBodyEmail] = useState("");
  const [notifiedBodyPhone, setNotifiedBodyPhone] = useState("");
  const [notifiedBodyWebsite, setNotifiedBodyWebsite] = useState("");
  const [notifiedBodyStreet, setNotifiedBodyStreet] = useState("");
  const [notifiedBodyPostCode, setNotifiedBodyPostCode] = useState("");
  const [notifiedBodyCity, setNotifiedBodyCity] = useState("");
  const [conformityModules, setConformityModules] = useState<string[]>([]);
  const [registrationNumber, setRegistrationNumber] = useState("");
  const { updateProject } = useUpdateProjectPayload(
    projectId,
    t("notification.saving.set_notified_body_success"),
    `/projects/${projectId}?saved=true`,
  );

  useEffect(() => {
    if (project?.notifiedBody?.name) {
      setNotifiedBodyName(project.notifiedBody.name);
    }
    if (project?.notifiedBody?.number) {
      setNotifiedBodyNumber(project.notifiedBody.number);
    }
    if (project?.notifiedBody?.address) {
      setNotifiedBodyAddress(project.notifiedBody.address);
    }
    if (project?.notifiedBody?.email) {
      setNotifiedBodyEmail(project.notifiedBody.email);
    }
    if (project?.notifiedBody?.phone) {
      setNotifiedBodyPhone(project.notifiedBody.phone);
    }
    if (project?.notifiedBody?.website) {
      setNotifiedBodyWebsite(project.notifiedBody.website);
    }
    if (project?.notifiedBody?.street) {
      setNotifiedBodyStreet(project.notifiedBody.street);
    }
    if (project?.notifiedBody?.postCode) {
      setNotifiedBodyPostCode(project.notifiedBody.postCode);
    }
    if (project?.notifiedBody?.city) {
      setNotifiedBodyCity(project.notifiedBody.city);
    }
    if (project?.notifiedBody?.conformityModules) {
      setConformityModules(project.notifiedBody.conformityModules);
    }
    if (project?.notifiedBody?.registrationNumber) {
      setRegistrationNumber(project.notifiedBody.registrationNumber);
    }
  }, [project]);

  // Generate options for ConformityModule enum
  const conformityModuleOptions = Object.values(ConformityModule).map(
    (module) => ({
      value: module,
      label: `${module} - ${t(`conformityModule.${module}.description`)}`,
    }),
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    updateProject({
      ...project!,
      notifiedBody: {
        ...project!.notifiedBody,
        required: true,
        name: notifiedBodyName,
        email: notifiedBodyEmail,
        number: notifiedBodyNumber,
        address: notifiedBodyAddress,
        phone: notifiedBodyPhone,
        website: notifiedBodyWebsite,
        street: notifiedBodyStreet,
        postCode: notifiedBodyPostCode,
        city: notifiedBodyCity,
        conformityModules: conformityModules,
        registrationNumber: registrationNumber,
      },
    });
  };

  return (
    <BasePage
      loading={fetchInProgress}
      breadcrumbItems={[
        { label: "Dashboard", location: "/dashboard" },
        {
          label: project?.projectdata?.product_name || projectId,
          location: `/projects/${projectId}`,
        },
        {
          label: t("notifiedBodies.label"),
          location: `/projects/${projectId}/notifiedBodies`,
        },
      ]}
    >
      <div>
        <div className="pb-5">
          <h3 className="text-3xl leading-6 font-medium text-gray-900">
            <Trans i18nKey={"notifiedBodies.label"}></Trans>
          </h3>
          <p className="text-sm text-gray-500 block pt-8 w-full">
            <Trans i18nKey={"notifiedBodies.subline"}></Trans>
          </p>
        </div>

        <div className="mt-6 space-y-6">
          {/* Link to EUR-Lex */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-3">
              {t("notifiedBodies.find.label")}
            </h2>
            <p className="text-sm text-gray-700 mb-4">
              <Trans i18nKey={"notifiedBodies.find.description"}></Trans>
            </p>
            <a
              href="https://webgate.ec.europa.eu/single-market-compliance-space/notified-bodies/notified-body-list?filter=legislationId:162400,notificationStatusId:1"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {t("notifiedBodies.find.link")}
              <ExternalLinkIcon className="ml-2 h-4 w-4" aria-hidden="true" />
            </a>
          </div>

          {/* Basic Contact Information Card */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              {t("notifiedBodies.add.label")}
            </h2>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="notifiedBodyName"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t("notifiedBodies.add.name")}
                </label>
                <input
                  type="text"
                  id="notifiedBodyName"
                  name="notifiedBodyName"
                  value={notifiedBodyName}
                  onChange={(e) => setNotifiedBodyName(e.target.value)}
                  placeholder={t("notifiedBodies.add.placeholder")}
                  className="shadow-sm focus:ring-cyan-500 focus:border-cyan-500 block w-full sm:text-sm border-gray-300 rounded-md px-3 py-2 border"
                />
              </div>
              <div>
                <label
                  htmlFor="notifiedBodyNumber"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t("notifiedBodies.add.number")}
                </label>
                <input
                  type="text"
                  id="notifiedBodyNumber"
                  name="notifiedBodyNumber"
                  value={notifiedBodyNumber}
                  onChange={(e) => setNotifiedBodyNumber(e.target.value)}
                  placeholder={t("notifiedBodies.add.number_placeholder")}
                  className="shadow-sm focus:ring-cyan-500 focus:border-cyan-500 block w-full sm:text-sm border-gray-300 rounded-md px-3 py-2 border"
                />
              </div>
              <div>
                <label
                  htmlFor="notifiedBodyAddress"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t("notifiedBodies.add.address")}
                </label>
                <input
                  type="text"
                  id="notifiedBodyAddress"
                  name="notifiedBodyAddress"
                  value={notifiedBodyAddress}
                  onChange={(e) => setNotifiedBodyAddress(e.target.value)}
                  placeholder={t("notifiedBodies.add.address_placeholder")}
                  className="shadow-sm focus:ring-cyan-500 focus:border-cyan-500 block w-full sm:text-sm border-gray-300 rounded-md px-3 py-2 border"
                />
              </div>
              <div>
                <label
                  htmlFor="notifiedBodyPostCode"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t("notifiedBodies.add.postCode")}
                </label>
                <input
                  type="text"
                  id="notifiedBodyPostCode"
                  name="notifiedBodyPostCode"
                  value={notifiedBodyPostCode}
                  onChange={(e) => setNotifiedBodyPostCode(e.target.value)}
                  placeholder={t("notifiedBodies.add.postCode_placeholder")}
                  className="shadow-sm focus:ring-cyan-500 focus:border-cyan-500 block w-full sm:text-sm border-gray-300 rounded-md px-3 py-2 border"
                />
              </div>
              <div>
                <label
                  htmlFor="notifiedBodyCity"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t("notifiedBodies.add.city")}
                </label>
                <input
                  type="text"
                  id="notifiedBodyCity"
                  name="notifiedBodyCity"
                  value={notifiedBodyCity}
                  onChange={(e) => setNotifiedBodyCity(e.target.value)}
                  placeholder={t("notifiedBodies.add.city_placeholder")}
                  className="shadow-sm focus:ring-cyan-500 focus:border-cyan-500 block w-full sm:text-sm border-gray-300 rounded-md px-3 py-2 border"
                />
              </div>
              <div>
                <label
                  htmlFor="notifiedBodyEmail"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t("notifiedBodies.add.email")}
                </label>
                <input
                  type="email"
                  id="notifiedBodyEmail"
                  name="notifiedBodyEmail"
                  value={notifiedBodyEmail}
                  onChange={(e) => setNotifiedBodyEmail(e.target.value)}
                  placeholder={t("notifiedBodies.add.email_placeholder")}
                  className="shadow-sm focus:ring-cyan-500 focus:border-cyan-500 block w-full sm:text-sm border-gray-300 rounded-md px-3 py-2 border"
                />
              </div>
              <div>
                <label
                  htmlFor="notifiedBodyPhone"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t("notifiedBodies.add.phone")}
                </label>
                <input
                  type="tel"
                  id="notifiedBodyPhone"
                  name="notifiedBodyPhone"
                  value={notifiedBodyPhone}
                  onChange={(e) => setNotifiedBodyPhone(e.target.value)}
                  placeholder={t("notifiedBodies.add.phone_placeholder")}
                  className="shadow-sm focus:ring-cyan-500 focus:border-cyan-500 block w-full sm:text-sm border-gray-300 rounded-md px-3 py-2 border"
                />
              </div>
              <div>
                <label
                  htmlFor="notifiedBodyWebsite"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t("notifiedBodies.add.website")}
                </label>
                <input
                  type="url"
                  id="notifiedBodyWebsite"
                  name="notifiedBodyWebsite"
                  value={notifiedBodyWebsite}
                  onChange={(e) => setNotifiedBodyWebsite(e.target.value)}
                  placeholder={t("notifiedBodies.add.website_placeholder")}
                  className="shadow-sm focus:ring-cyan-500 focus:border-cyan-500 block w-full sm:text-sm border-gray-300 rounded-md px-3 py-2 border"
                />
              </div>
            </div>
          </div>

          {/* Module and Registration Details Card */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              {t("notifiedBodies.moduleDetails.label")}
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              {t("notifiedBodies.moduleDetails.description")}
            </p>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="registrationNumber"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t("notifiedBodies.add.registrationNumber")}
                </label>
                <input
                  type="text"
                  id="registrationNumber"
                  name="registrationNumber"
                  value={registrationNumber}
                  onChange={(e) => setRegistrationNumber(e.target.value)}
                  placeholder={t(
                    "notifiedBodies.add.registrationNumber_placeholder",
                  )}
                  className="shadow-sm focus:ring-cyan-500 focus:border-cyan-500 block w-full sm:text-sm border-gray-300 rounded-md px-3 py-2 border"
                />
              </div>
              <MultiSelectInput
                id="conformityModules"
                name="conformityModules"
                label="notifiedBodies.add.conformityModules"
                options={conformityModuleOptions}
                defaultValue={conformityModules.join(";")}
                onBlur={(e) => setConformityModules(e.target.value.split(";"))}
              />
            </div>
          </div>
        </div>

        <div className="pt-9 text-right flex justify-end items-center">
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={!notifiedBodyName}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t("notifiedBodies.add.save")}
          </button>
        </div>
      </div>
    </BasePage>
  );
};

export default NotifiedBodies;
